import React, { useState, useEffect, useMemo } from 'react';
import { Column } from 'styled/alignment/Column';
import { useForm, Controller } from 'react-hook-form';
import colors from 'theme/colors';
import fieldValidations from "helpers/fieldValidations";
import styled from 'styled-components';
import breakpoints from 'theme/breakpoints';
import Loader from 'components/_UI/Loader';
import Text from 'components/_UI/Text';
import Fieldset from 'components/_UI/Fieldset';
import masks from 'helpers/masks';
import unformat from 'helpers/unformat';
import currency from 'helpers/currency';
import serviceFee from 'helpers/serviceFee';
import { useCheckout } from 'context/checkout/Checkout';
import ReCAPTCHA from "react-google-recaptcha";
import { Row } from 'styled/alignment/Row';
import ordersApi from 'api/requests/orders';
import Select from 'react-select';
import { CreditCard as CreditCardIcon, ShoppingBag, Lock } from 'lucide-react';

function aggregateItems(tickets) {
    if (!tickets?.length) return [];
    const map = new Map();
    tickets.forEach(item => {
        const key = item.id;
        if (map.has(key)) {
            const existing = map.get(key);
            existing.quantity += 1;
        } else {
            map.set(key, {
                id: item.id,
                name: item.name,
                price: item.price,
                quantity: 1,
                absorbed_service_fee: item.absorbed_service_fee,
                service_fee: item.service_fee,
                service_fees: item.service_fees,
            });
        }
    });
    return Array.from(map.values());
}

export default function CreditcardForm({ onTrigger, errorRequest, setErrorRequest, tickets = [], totalPrice = 0 }) {
    const [loading, setLoading] = useState(false);
    const { checkout, setCheckout } = useCheckout();
    const [isRecaptcha, setIsRecaptcha] = useState(false)
    const [tokenRecaptcha, setTokenRecaptcha] = useState(null)
    const [installments, setInstallments] = useState([])

    const items = useMemo(() => aggregateItems(tickets), [tickets]);
    const serviceFeeTotal = useMemo(() => items.reduce((acc, item) => (
        acc + serviceFee.perUnit(item, serviceFee.METHODS.CREDIT_CARD, item.absorbed_service_fee) * item.quantity
    ), 0), [items]);

    const {
        register,
        reset,
        handleSubmit,
        control,
        watch,
        formState: { errors },
    } = useForm({
        mode: 'onBlur',
        defaultValues: {
            installments: null,
        }
    });

    const selectedInstallmentValue = watch('installments');
    const selectedInstallment = useMemo(
        () => installments.find(opt => opt.value === selectedInstallmentValue),
        [installments, selectedInstallmentValue]
    );
    const installmentFee = useMemo(() => {
        if (!selectedInstallment) return 0;
        const totalWithFee = selectedInstallment.installment_value * selectedInstallment.installments;
        const fee = totalWithFee - totalPrice;
        return fee > 0 ? fee : 0;
    }, [selectedInstallment, totalPrice]);
    const finalTotal = totalPrice + installmentFee;

    useEffect(async () => {
        if (checkout.orderId) {
            const { data: { data } } = await ordersApi.getInstallments(checkout.orderId, 'credit_card')
            setInstallments(rawInstallmentsToSelect(data))
        }
    }, [checkout.orderId])

    useEffect(() => {
        if (installments && installments.length > 0) {
            reset({
                installments: installments[0].value,
            });
        }
    }, [installments, reset])

    useEffect(() => {
        if (errorRequest >= 3) {
            setIsRecaptcha(true)
        }
    }, [errorRequest])

    function rawInstallmentsToSelect(installments) {
        return installments?.map(installment => {
            return {
                ...installment,
                label: `${installment.installments}x de ${currency.format(installment.installment_value)} ${installment.tax_rate ? 'com juros' : 'sem juros'}`,
                value: installment.installments
            }
        });
    }

    function handlePayment(data) {
        window.trigger = Math.random(Infinity);

        const payload = {
            document: unformat.getNumbers(data?.document),
            creditcardNumber: unformat.getNumbers(data?.creditcardNumber),
            date: data?.date,
            holder: data?.holder,
            code: data?.code,
            installments: data?.installments,
            grecaptcha_response: tokenRecaptcha
        }

        onTrigger(payload);
    }

    const isDisabled = errorRequest >= 3 && !tokenRecaptcha;

    return (
        <FormSection>
            <SectionHeader>
                <SectionIcon>
                    <CreditCardIcon size={16} color={colors.occasionalPurple} />
                </SectionIcon>
                <Text type='bold' paddingBottom={0} color={colors.nightDark}>
                    Dados do cartão
                </Text>
            </SectionHeader>

            <Column>
                {
                    !!installments.length ?
                        <>
                            <FieldGrid>
                                <Column>
                                    <Fieldset
                                        marginTop='20px'
                                        label='Nome escrito no cartão *'
                                        layout='squired'
                                        name="holder"
                                        register={register}
                                        validations={fieldValidations.holderName('Nome escrito no cartão')}
                                    />

                                    {errors.holder?.message && (
                                        <Column>
                                            <Text color={colors.danger} name='small'> {errors.holder?.message} </Text>
                                        </Column>
                                    )}
                                </Column>

                                <Column>
                                    <Fieldset
                                        marginTop='20px'
                                        label='CPF *'
                                        layout='squired'
                                        name="document"
                                        register={register}
                                        mask={masks.cpf}
                                        validations={fieldValidations.cpf()}
                                    />

                                    {errors.document?.message && (
                                        <Column>
                                            <Text color={colors.danger} name='small'> {errors.document?.message} </Text>
                                        </Column>
                                    )}
                                </Column>
                            </FieldGrid>

                            <FieldGrid>
                                <Column>
                                    <Fieldset
                                        marginTop='20px'
                                        label='Número do cartão *'
                                        layout='squired'
                                        name="creditcardNumber"
                                        register={register}
                                        mask={masks.creditCard}
                                        validations={fieldValidations.creditCard('Número do cartão')}
                                    />

                                    {errors.creditcardNumber?.message && (
                                        <Column>
                                            <Text color={colors.danger} name='small'> {errors.creditcardNumber?.message} </Text>
                                        </Column>
                                    )}
                                </Column>

                                <Column>
                                    <Fieldset
                                        marginTop='20px'
                                        label='Data de expiração *'
                                        name="date"
                                        register={register}
                                        validations={fieldValidations.cardExpiration('Data de expiração')}
                                        layout='squired'
                                        mask={masks.dateExpiredCardCredit}
                                    />

                                    {errors.date?.message && (
                                        <Column>
                                            <Text color={colors.danger} name='small'> {errors.date?.message} </Text>
                                        </Column>
                                    )}
                                </Column>
                            </FieldGrid>

                            <FieldGrid>
                                <Column>
                                    <Fieldset
                                        marginTop='20px'
                                        label='CVV *'
                                        name="code"
                                        register={register}
                                        validations={fieldValidations.cvv()}
                                        layout='squired'
                                    />

                                    {errors.code?.message && (
                                        <Column>
                                            <Text color={colors.danger} name='small'> {errors.code?.message} </Text>
                                        </Column>
                                    )}
                                </Column>

                                <Column>
                                    <Text
                                        color={colors.nightDark}
                                        name='small'
                                        type='bold'
                                        marginTop={'20px'}
                                    >
                                        Opções de parcelamento *
                                    </Text>

                                    <Controller
                                        name="installments"
                                        control={control}
                                        rules={{ required: 'Selecione uma opção de parcelamento' }}
                                        render={({ field }) => (
                                            <Select
                                                styles={{
                                                    dropdownIndicator: (base) => ({
                                                        ...base,
                                                        margin: 0,
                                                    }),
                                                    control: (base) => ({
                                                        ...base,
                                                        borderColor: colors.nightLight,
                                                    }),
                                                    container: (base) => ({
                                                        ...base,
                                                        margin: '0 !important',
                                                    }),
                                                }}
                                                isSearchable={false}
                                                placeholder='Opções de parcelamento'
                                                value={installments.find(option => option.value === field.value) || null}
                                                maxMenuHeight={200}
                                                options={installments}
                                                onChange={(selectedOption) => field.onChange(selectedOption?.value)}
                                            />
                                        )}
                                    />
                                </Column>
                            </FieldGrid>

                            {isRecaptcha && (
                                <Row marginTop={'15px'} horizontalCenter marginBottom={'15px'}>
                                    <ReCAPTCHA
                                        sitekey={import.meta.env.VITE_APP_SITE_KEY}
                                        onChange={(e) => setTokenRecaptcha(e)}
                                    />
                                </Row>
                            )}

                            {items.length > 0 && (
                                <OrderSummary>
                                    <SummaryHeader>
                                        <ShoppingBag size={14} color={colors.night} />
                                        <SummaryTitle>Resumo do pedido</SummaryTitle>
                                    </SummaryHeader>

                                    {items.map(item => (
                                        <SummaryRow key={item.id}>
                                            <SummaryItemName>
                                                {item.quantity > 1 ? `${item.quantity}x ` : ''}{item.name}
                                            </SummaryItemName>
                                            <SummaryItemPrice>
                                                {currency.format(item.price * item.quantity)}
                                            </SummaryItemPrice>
                                        </SummaryRow>
                                    ))}

                                    {serviceFeeTotal > 0 && (
                                        <SummaryRow>
                                            <SummaryItemName>Taxa de serviço</SummaryItemName>
                                            <SummaryItemPrice>{currency.format(serviceFeeTotal)}</SummaryItemPrice>
                                        </SummaryRow>
                                    )}

                                    {installmentFee > 0 && (
                                        <SummaryRow>
                                            <SummaryItemName>Taxa de parcelamento</SummaryItemName>
                                            <SummaryItemPrice>{currency.format(installmentFee)}</SummaryItemPrice>
                                        </SummaryRow>
                                    )}

                                    <SummaryDivider />

                                    <SummaryRow>
                                        <SummaryTotal>Total</SummaryTotal>
                                        <SummaryTotalValue>{currency.format(finalTotal)}</SummaryTotalValue>
                                    </SummaryRow>
                                </OrderSummary>
                            )}

                            {
                                loading
                                    ? <Loader />
                                    : (
                                        <PayButton
                                            disabled={isDisabled}
                                            onClick={handleSubmit(handlePayment)}
                                        >
                                            <Lock size={16} color={colors.neutralLight} />
                                            <PayButtonText>Efetuar pagamento</PayButtonText>
                                        </PayButton>
                                    )
                            }
                        </>
                        : <Loader  />
                }
            </Column>
        </FormSection>
    );
}

const FormSection = styled.div`
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    padding: 24px 20px;
    margin-top: 16px;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 28px 28px;
    }
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
`;

const SectionIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #F3EAFF;
    flex-shrink: 0;
`;

const FieldGrid = styled.div`
    display: grid;
    gap: 12px;
    grid-template-columns: 1fr;

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: 1fr 1fr;
    }
`;

const OrderSummary = styled.div`
    background: ${colors.night}06;
    border: 1px solid ${colors.night}11;
    border-radius: 10px;
    padding: 14px 16px;
    margin-top: 20px;
`;

const SummaryHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 10px;
`;

const SummaryTitle = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${colors.night};
    text-transform: uppercase;
    letter-spacing: 0.3px;
`;

const SummaryRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 3px 0;
`;

const SummaryItemName = styled.span`
    font-size: 13px;
    color: ${colors.night};
`;

const SummaryItemPrice = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.night};
`;

const SummaryDivider = styled.div`
    height: 1px;
    background: ${colors.night}15;
    margin: 8px 0;
`;

const SummaryTotal = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const SummaryTotalValue = styled.span`
    font-size: 15px;
    font-weight: 600;
    color: ${colors.occasionalPurple};
`;

const PayButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 14px;
    margin-top: 16px;
    margin-bottom: 16px;
    border-radius: 10px;
    background: ${colors.occasionalPurple};
    border: none;
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover {
        opacity: 0.9;
    }

    ${({ disabled }) => disabled && `
        background: ${colors.neutralDark};
        cursor: not-allowed;

        &:hover {
            opacity: 1;
        }
    `}
`;

const PayButtonText = styled.span`
    font-size: 15px;
    font-weight: 600;
    color: ${colors.neutralLight};
`;
